import { NgFor, NgIf, SlicePipe } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute, Router } from "@angular/router";
import { firstValueFrom, Subscription } from "rxjs";
import { KebabCasePipe } from "src/app/pipes/kebab-case.pipe";
import { ConfigService } from "src/app/servicios/config/config.service";
import { NoticiasService } from "../../servicios/noticias/noticias.service";
import { NoticiaListadoComponent } from "../noticia-listado/noticia-listado.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Noticia } from "@src/app/models/interfaces/noticia.interface";

@Component({
  selector: "app-buscador",
  templateUrl: "./buscador.component.html",
  styleUrls: ["./buscador.component.css"],
  imports: [NoticiaListadoComponent, FormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, SlicePipe, MatProgressSpinnerModule, NgIf, NgFor],
})
export class BuscadorComponent implements OnInit, OnDestroy {
  /** Slug extraído de la URL para la búsqueda */
  slugBusqueda: any;
  /** Texto de búsqueda derivado del slug (guiones reemplazados por espacios) */
  textoBusqueda: any;
  /** Lista de noticias resultantes de la búsqueda */
  noticias: Noticia[] = [];
  /** URL actual del componente, obtenida desde la ruta */
  url: any;
  /** Constantes globales de la aplicación */
  constantesGlobales: any;
  /** Suscripción a los parámetros de la ruta */
  rSub: Subscription;
  /** Indicador de carga para la búsqueda */
  loading: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private noticiasService: NoticiasService,
    private router: Router,
    private kebabPipe: KebabCasePipe,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * Inicializa el componente:
   * - Obtiene la URL y parámetros de la ruta.
   * - Extrae el slug de búsqueda y lo transforma en texto.
   * - Obtiene las constantes globales y realiza la búsqueda si se proporciona un slug.
   */
  ngOnInit(): void {
    this.url = this.activatedRoute.snapshot.data.url;
    this.rSub = this.activatedRoute.params.subscribe((parms: any) => {
      this.slugBusqueda = parms.id;
      this.textoBusqueda = this.slugBusqueda.replaceAll("-", " ");
      this.constantesGlobales = this.configService.getConstantesGlobales();

      if (parms.id) {
        this.fetchNewsAndStoreKey(this.slugBusqueda);
      }
    });
  }

  /**
   * Obtiene las últimas noticias basadas en un término de búsqueda y las almacena en el estado del componente.
   *
   * @param searchTerm - El término de búsqueda utilizado para filtrar las noticias.
   * @returns Una promesa que se resuelve cuando las noticias han sido obtenidas y almacenadas.
   *
   * @remarks
   * Este método establece el indicador de carga (`loading`) en `true` mientras se realiza la solicitud.
   * Utiliza el servicio `noticiasService` para obtener las noticias y maneja posibles errores
   * devolviendo un arreglo vacío en caso de fallo.
   */
  async fetchNewsAndStoreKey(searchTerm: string): Promise<void> {
    this.loading = true;
    const lastNews = this.noticiasService.getUltimasNoticias(50, "all", searchTerm, this.constantesGlobales.idAmbito);

    if (lastNews) {
      try {
        const result = await firstValueFrom(lastNews);
        this.setNews(result);
      } catch {
        this.setNews([]);
      }
    }
  }

  /**
   * Actualiza la lista de noticias y oculta el indicador de carga.
   * Fuerza la actualización de la vista.
   * @param noticias Lista de noticias obtenidas.
   */
  setNews(noticias) {
    this.noticias = noticias;
    this.loading = false;
    this.cdr.detectChanges(); // Fuerza la actualización de la vista
  }

  /**
   * Limpia la suscripción a los parámetros de la ruta para evitar fugas de memoria.
   */
  ngOnDestroy(): void {
    if (this.rSub) {
      this.rSub.unsubscribe();
    }
  }

  /**
   * Envía la búsqueda navegando a la ruta correspondiente,
   * transformando el término de búsqueda a formato kebab-case.
   * @param data Objeto que contiene el término de búsqueda.
   */
  enviarBusqueda(data): void {
    const busqueda = "/buscar/" + this.kebabPipe.transform(data.buscar);
    this.router.navigateByUrl(busqueda);
  }

  trackByNoticia(index: number, noticia: Noticia): string {
    return noticia.idVideo;
  }
}
